import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import * as api from '../services/api';

interface UploadState {
  loading: boolean;
  jobId: string | null;
  error: string | null;
  jobStatus: string | null;
  jobProgress: number;
  jobResultUrl: string | null;
  jobError: string | null;
}

const initialState: UploadState = {
  loading: false,
  jobId: null,
  error: null,
  jobStatus: null,
  jobProgress: 0,
  jobResultUrl: null,
  jobError: null,
};

const MAX_FILE_SIZE = 4 * 1024 * 1024 * 1024; // 4 GB

export const submitHDF5Upload = createAsyncThunk(
  'upload/submit',
  async ({ file, subjectId }: { file: File; subjectId?: string }, { rejectWithValue }) => {
    try {
      if (file.size > MAX_FILE_SIZE) {
        return rejectWithValue(`File too large (${(file.size / (1024 * 1024 * 1024)).toFixed(1)} GB). Maximum is 4 GB.`);
      }
      const presignRes = await api.presignUpload({ filename: file.name, content_type: file.type });

      // TODO: better loggging
      // console.log('📡 Presign Response:', presignRes);

      const presigned_url = presignRes?.presigned_url || presignRes?.upload_url || presignRes?.url;
      const s3_key = presignRes?.s3_key || presignRes?.key;
      const file_uuid = presignRes?.file_uuid || presignRes?.uuid || presignRes?.id;

      // console.log("📤 Uploading to S3:", presigned_url);

      await fetch(presigned_url, {
        method: 'PUT',
        // Note: adding header would have conflict with s3
        // headers: {
        // 'Content-Type': file.type,
        // },
        body: file,
      });

      const submitJobRes = await api.submitJob({ s3_key, uuid: file_uuid, subject_id: subjectId || undefined })
      const job_id = submitJobRes.job_id;

      // console.log('🚀 Job submitted:', job_id);

      return { job_id };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Upload failed');
    }
  }
);

export const pollJobStatus = createAsyncThunk(
  'upload/pollStatus',
  async (job_id: string, { rejectWithValue }) => {
    try {
      const res = await api.getJobStatus(job_id);
      return res;
    } catch (err:any) {
      return rejectWithValue(err.message || 'Plling failed');
    }
  }
);

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    resetUpload: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(submitHDF5Upload.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.jobStatus = null;
      state.jobProgress = 0;
      state.jobResultUrl = null;
      state.jobError = null;
    });
    builder.addCase(submitHDF5Upload.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.jobId = payload.job_id;
    });
    builder.addCase(submitHDF5Upload.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload as string;
    });
    builder.addCase(pollJobStatus.fulfilled, (state, { payload }) => {
      state.jobStatus = payload.status;
      const progressMap: Record<string, number> = {
        CREATED: 5,
        QUEUED: 10,
        PROVISIONING: 20,
        INITIALIZING: 35,
        RUNNING: 50,
        SUCCEEDED: 100,
        FAILED: 100,
      };
      state.jobProgress = progressMap[payload.status] ?? state.jobProgress;
      state.jobResultUrl = payload.result_uri ?? null;
      state.jobError = payload.error_message ?? null;
    });
    builder.addCase(pollJobStatus.rejected, (state, { payload }) => {
      state.jobError = payload as string;
    })
  },
});

export const { resetUpload } = uploadSlice.actions;
export default uploadSlice.reducer;

export const selectUploadState = (state: RootState) => state.upload;
